import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AlertTriangle, AlertCircle, Info, X, Minimize2, Maximize2, Bell, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { bannerApi } from '../../lib/bannerApi';
import { useAuth } from '../../contexts/AuthContext';

const SEVERITY_STYLES = {
  critical: {
    bar: 'bg-red-600 text-white border-red-700',
    icon: AlertCircle,
    chip: 'bg-red-600 text-white hover:bg-red-700',
    pulse: 'bg-red-400',
    dot: 'bg-red-500',
  },
  warning: {
    bar: 'bg-amber-50 text-amber-900 border-amber-300',
    icon: AlertTriangle,
    chip: 'bg-amber-500 text-white hover:bg-amber-600',
    pulse: 'bg-amber-300',
    dot: 'bg-amber-500',
  },
  info: {
    bar: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    icon: Info,
    chip: 'bg-indigo-600 text-white hover:bg-indigo-700',
    pulse: 'bg-indigo-300',
    dot: 'bg-indigo-500',
  },
};

const POLL_INTERVAL_MS = 60_000;

function getApiBase() {
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
}

export function GlobalBannerStrip() {
  const { user, currentLocation } = useAuth();
  const [banners, setBanners] = useState([]);
  const pollRef = useRef(null);
  const sseRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await bannerApi.getActive(currentLocation?.id);
      setBanners(resp.data?.banners || []);
    } catch {
      setBanners([]);
    }
  }, [user, currentLocation?.id]);

  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    if (!user) return;
    load();

    // Same-tab instant update — BannerManagementPage dispatches this after create/update/delete
    const onBannerChanged = () => setTimeout(() => loadRef.current(), 200);
    window.addEventListener('banner_changed', onBannerChanged);

    // SSE for cross-tab/cross-user updates
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const url = `${getApiBase()}/api/events/system?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        es.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data?.type === 'banner_changed') onBannerChanged();
          } catch { /* keepalive */ }
        };
        sseRef.current = es;
      } catch { /* fallback to polling */ }
    }

    pollRef.current = setInterval(load, POLL_INTERVAL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('banner_changed', onBannerChanged);
      window.removeEventListener('focus', onFocus);
      clearInterval(pollRef.current);
      if (sseRef.current) { try { sseRef.current.close(); } catch {} sseRef.current = null; }
    };
  }, [user, load]);

  const setLocal = (id, patch) => {
    setBanners(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
  };
  const removeLocal = (id) => setBanners(prev => prev.filter(b => b.id !== id));

  const handleMinimize = async (b) => {
    setLocal(b.id, { minimized: true });
    try { await bannerApi.ack(b.id, 'minimize'); } catch { setLocal(b.id, { minimized: false }); }
  };
  const handleExpand = async (b) => {
    setLocal(b.id, { minimized: false });
    try { await bannerApi.ack(b.id, 'expand'); } catch { setLocal(b.id, { minimized: true }); }
  };
  const handleAcknowledge = async (b) => {
    removeLocal(b.id);
    try { await bannerApi.ack(b.id, 'acknowledge'); } catch { load(); }
  };

  if (!user || !banners.length) return null;

  const expanded = banners.filter(b => !b.minimized);
  const minimized = banners.filter(b => b.minimized);

  return (
    <>
      {/* Expanded full-width strips, stacked at the very top */}
      {expanded.length > 0 && (
        <div className="w-full" data-testid="banner-strip">
          {expanded.map(b => {
            const cfg = SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.info;
            const Icon = cfg.icon;
            return (
              <div
                key={b.id}
                className={cn('w-full px-4 py-2.5 border-b flex items-start gap-3', cfg.bar)}
                data-testid={`banner-${b.id}`}
                role="alert"
              >
                <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold leading-tight">{b.title}</p>
                  <p className="text-[12px] mt-0.5 leading-relaxed opacity-90 whitespace-pre-wrap">{b.message}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleMinimize(b)}
                    className={cn(
                      'p-1.5 rounded transition-colors',
                      b.severity === 'critical' ? 'hover:bg-red-700' : 'hover:bg-black/5'
                    )}
                    title="Minimize — keep me reminded"
                    data-testid={`banner-minimize-${b.id}`}
                  >
                    <Minimize2 className="h-3.5 w-3.5" />
                  </button>
                  {b.dismissible && (
                    <button
                      onClick={() => handleAcknowledge(b)}
                      className={cn(
                        'p-1.5 rounded transition-colors',
                        b.severity === 'critical' ? 'hover:bg-red-700' : 'hover:bg-black/5'
                      )}
                      title="Acknowledge — dismiss this banner"
                      data-testid={`banner-acknowledge-${b.id}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Minimized chips: floating bottom-right with pulsing dot */}
      {minimized.length > 0 && (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 items-end" data-testid="banner-minimized-stack">
          {minimized.map(b => {
            const cfg = SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.info;
            const Icon = cfg.icon;
            return (
              <button
                key={b.id}
                onClick={() => handleExpand(b)}
                className={cn(
                  'group relative flex items-center gap-2 pl-3 pr-3 py-2 rounded-full shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5',
                  cfg.chip
                )}
                title={`${b.title} — click to expand`}
                data-testid={`banner-chip-${b.id}`}
              >
                {/* Pulsing dot */}
                <span className="relative flex h-2.5 w-2.5">
                  <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full opacity-75', cfg.pulse)} />
                  <span className={cn('relative inline-flex rounded-full h-2.5 w-2.5', cfg.dot, 'ring-2 ring-white/60')} />
                </span>
                <Icon className="h-3.5 w-3.5" />
                <span className="text-[11px] font-bold tracking-wide max-w-[200px] truncate">{b.title}</span>
                <Maximize2 className="h-3 w-3 opacity-70 group-hover:opacity-100" />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

export default GlobalBannerStrip;
